/**
 * save/serialize.ts — convert a live GameState into a JSON-safe SaveBlob.
 *
 * Per MULTIPLAYER §6.1 (v2M2): v1 single-player saves are JSON; the
 * MP-era binary format will sit behind the same function pair so call
 * sites don't need to change.
 *
 * The serialiser drops the three static catalogues (shipTemplates,
 * weapons, techs) — those live in ships.json + tech-tree.json shipped
 * with the game and are re-attached on load from a freshly-loaded
 * LoadedGameData. This keeps saves small (a typical mid-game save
 * comes in well under 1MB) and protects against catalogue drift between
 * sessions: if the spreadsheets ship a balance change, every save
 * inherits it on load rather than freezing the old numbers in place.
 *
 * Map / Set instances serialise as arrays. The deserialiser
 * (save/deserialize.ts) is the inverse.
 */

import type {
  Empire,
  EnvironmentEntity,
  GameState,
  Missile,
  Planet,
  ShipInstance,
  StarSystem,
  Structure,
  VisibilityRecord,
  WarpGateLink,
} from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';
import type { RngState } from '../state/rng';

/**
 * Sentinel for `WeaponSlotState.ammoRemaining === Infinity`. Non-Concussion
 * weapons carry Infinity ammo (MECHANICS §3); JSON.stringify silently turns
 * Infinity into null, so we substitute a reserved negative-one in the
 * serialised form. The deserialiser restores Infinity. ammoRemaining is
 * otherwise non-negative, so -1 is a safe sentinel.
 */
const INF_AMMO_SENTINEL = -1;

/**
 * Save-blob format version. Bump this on any breaking change to the
 * SaveBlob shape; deserialiser refuses mismatched versions with a
 * helpful error rather than silently producing a broken state.
 *
 * Distinct from `state.version` (SAVE_FORMAT_VERSION in types.ts),
 * which versions the live GameState shape itself. The two will
 * usually move in lockstep but are conceptually independent — a
 * blob layout change (e.g. adding a header field) doesn't require
 * a state-shape bump.
 */
/**
 * MFix1.4 bumped this from 1 → 2 alongside SAVE_FORMAT_VERSION when
 * PlanetSize switched to a number. deserialize.ts checks this on
 * load and throws SaveLoadError ("Saves can't be migrated
 * automatically") on mismatch.
 *
 * MFix3.4 bumped 2 → 3 alongside SAVE_FORMAT_VERSION when
 * `pendingColoniseTargetId` was added to ShipInstance.
 *
 * v3M7.1 bumped 3 → 4 alongside SAVE_FORMAT_VERSION when
 * `aiDifficulty` + `aiBehaviour` were added to Empire.
 *
 * v3M2.2b bumped 4 → 5 covering ODP ring state + Minefield runtime.
 *
 * MFix6.3 bumped 5 → 6 alongside SAVE_FORMAT_VERSION when
 * `scoutedSystemIds` was added to Empire (galaxy-view fog-of-war).
 *
 * v3M18.1 bumped 6 → 7 alongside SAVE_FORMAT_VERSION when
 * `warpGateLinks` was added to GameState.
 *
 * v3M18.5 bumped 7 → 8 alongside SAVE_FORMAT_VERSION when
 * `jumpTargetGateId` was added to ShipInstance for warp-link arrival.
 *
 * v3M18.5 fix-up bumped 8 → 9 alongside SAVE_FORMAT_VERSION when
 * `pendingWarpGateLinkId` was added to ShipInstance for sail-then-jump.
 *
 * v3M17.1 bumped 9 → 10 alongside SAVE_FORMAT_VERSION for the carrier
 * fighter launch data model — six new optional ShipInstance fields
 * (carrier embarked-squadron state + per-carrier behaviour settings +
 * squadron tracking). Optional fields round-trip via the existing
 * `serializeShip` `...spread` path, no fixture sweep, but the version
 * bump signals the schema delta to consumers reading saves.
 *
 * v3M22.1 bumped 10 → 11 for the ship-commands data model — four new
 * required ShipInstance fields (`commandKind`, `waypoints`,
 * `guardTargetKind`, `guardTargetId`). Required (not optional) so the
 * sim never has to defend against absent fields; ~30-file fixture
 * sweep covers existing test fixtures.
 *
 * v3M21.1 bumped 11 → 12 for the diplomacy data model
 * (`DIPLOMACY.md §11`). New fields land all at once so subsequent
 * v3M21.N sub-checkpoints don't re-bump:
 *   - `Empire.diplomaticFocus: PlanetId | null`
 *   - `Planet.influence: Map<EmpireId, number>` (serialised as a record)
 *   - `Planet.federatedWith: EmpireId | null`
 *   - `Planet.tradingWith: EmpireId[]`
 *   - `Planet.vassalOf: EmpireId | null`
 *   - `state.empires` includes a synthetic neutral empire row at
 *     `EMPIRE_NEUTRAL_ID = 0`.
 * Required (not optional) so the sim never defends against absent
 * fields. Pre-v12 saves are hard-rejected per the established pattern.
 */
/**
 * v3M28.1 — bumped 12 → 13 when `state.missiles: Map<MissileId, Missile>`
 * was added. Serialised as a flat array of `[id, Missile]` pairs and
 * restored back to a Map on deserialise. Pre-v13 saves are hard-
 * rejected per the established pattern (no migration).
 */
/**
 * v4M5.1 — bumped 13 → 14 to enforce the `docs/HYPERSPACE.md §7` hard
 * break: pre-v4M5 saves are deliberately not migrated. Covers both the
 * v4M3.1 schema delta (Empire.teamId — back-filled at deserialise so
 * saves *could* have loaded, but the SAVE_BLOB_VERSION bump was omitted
 * in that commit) and the v4M5.1 ShipTemplate / xlsx schema rework.
 * Players hold an Alpha 0.5 / v4M3-build tab open to continue any
 * in-progress save; v4M5 builds start fresh.
 */
/**
 * v4M5.3a — bumped 14 → 15 alongside SAVE_FORMAT_VERSION 15 → 16. New
 * `chargeUpTicksRemaining` + `jumpFuelCostPaid` fields on `ShipInstance`
 * make charge-up an on-map vulnerable phase per `HYPERSPACE.md §6`.
 * Pre-v15 blobs can't represent the new state — bump enforces fail-fast.
 */
/**
 * v4M5.3b1 — bumped 15 → 16 alongside SAVE_FORMAT_VERSION 16 → 17. New
 * `pendingJumpSystemId` + `pendingJumpArrivalAngle` fields on
 * `ShipInstance` for the continuous-Jump-Area dispatch model per
 * `HYPERSPACE.md §4`. v4M5.3b1 was additive (legacy `pendingJumpPointId`
 * stayed on the type).
 *
 * v4M5.3b3a — bumped 16 → 17 alongside SAVE_FORMAT_VERSION 17 → 18.
 * Legacy `pendingJumpPointId` dropped; `jumpTargetJumpPointId`
 * replaced by `jumpArrivalAngle: number | null`. Appliers now consume
 * `targetSystemId` + `arrivalAngle` natively; the v4M5.3b2
 * `resolveJumpPointForCommand` adapter is gone.
 *
 * v4M5.3b3c — bumped 17 → 18 alongside SAVE_FORMAT_VERSION 18 → 19.
 * `state.jumpPoints: Map<JumpPointId, JumpPoint>` retired entirely
 * (no more serialised entry). `StarSystem.adjacentSystemIds` collapsed to
 * `StarSystem.adjacentSystemIds: SystemId[]` — symmetric system-pair
 * adjacency rides on the StarSystem record itself.
 */
export const SAVE_BLOB_VERSION = 18;

/**
 * Serialised Empire — Sets converted to arrays, everything else as-is.
 * `scoutedSystemIds` (MFix6.3) is the second Set field on Empire and
 * follows the same pattern as `researchedTechs`.
 */
export interface SerializedEmpire
  extends Omit<Empire, 'researchedTechs' | 'scoutedSystemIds'> {
  researchedTechs: string[];
  scoutedSystemIds: number[];
}

/**
 * Serialised Planet — `influence: Map<EmpireId, number>` is flattened
 * to an array of `[empireId, points]` pairs (same pattern as
 * `SerializedTeamVision`'s nested-Map flattening). Other Planet fields
 * are JSON-clean and ride through as-is. v3M21.1.
 */
export interface SerializedPlanet extends Omit<Planet, 'influence'> {
  influence: Array<[number, number]>;
}

/** Serialised TeamVision — outer Map of inner Map flattened to array of arrays. */
export type SerializedTeamVision = Array<
  [number, Array<[number, VisibilityRecord]>]
>;

/**
 * The shape that ends up in localStorage / on disk. Plain JSON: no
 * Maps, no Sets, no class instances.
 */
export interface SerializedGameState {
  version: number;
  tick: number;
  rng: RngState;
  nextEntityId: number;
  empires: Array<[number, SerializedEmpire]>;
  planets: Array<[number, SerializedPlanet]>;
  systems: Array<[number, StarSystem]>;
  /** v3M18.1 — explicit player-authored Warp Gate connections. */
  warpGateLinks: Array<[number, WarpGateLink]>;
  ships: Array<[number, ShipInstance]>;
  /**
   * v3M28.1 — persistent missile entities in flight. Each entry round-
   * trips intact (no nested Maps/Sets on the Missile type, so a plain
   * tuple serialise is sufficient). Empty array for saves taken
   * outside of combat.
   */
  missiles: Array<[number, Missile]>;
  structures: Array<[number, Structure]>;
  environment: Array<[number, EnvironmentEntity]>;
  teamVision: SerializedTeamVision;
  playerEmpireId: number;
  /**
   * v3M12 — saved/loaded so a finished game stays finished. Older
   * blobs without this field default to `'in-progress'` on load.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

/**
 * Save-blob header. Player-facing slot pickers read these fields to
 * render a meaningful "what's in this slot" line without parsing the
 * full body.
 */
export interface SaveBlobHeader {
  formatVersion: number;
  /** Wall-clock timestamp at save time (ms since epoch). */
  savedAt: number;
  /** Sim tick at save time — useful for "Game time: 12:34" displays. */
  tick: number;
  /** Player's empire id (so the slot picker can show the player's race). */
  playerEmpireId: number;
  /** Number of non-player empires at save time. */
  aiOpponentCount: number;
  /**
   * Optional player-supplied label (e.g. "before invasion"). Slots use
   * this in the UI; quicksave leaves it null.
   */
  label: string | null;
  /**
   * v3M12 — surfaced at header level so the save slot picker can show
   * a victory / defeat badge without parsing the full body. Older
   * blobs without this field render as in-progress.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

export interface SaveBlob {
  header: SaveBlobHeader;
  body: SerializedGameState;
}

/** Replace Infinity ammo with the reserved sentinel for JSON safety. */
function serializeShip(s: ShipInstance): ShipInstance {
  return {
    ...s,
    weaponSlots: s.weaponSlots.map((w) => ({
      cooldownTicks: w.cooldownTicks,
      ammoRemaining: w.ammoRemaining === Infinity ? INF_AMMO_SENTINEL : w.ammoRemaining,
    })),
  };
}

/** Convert one Empire into its serialised shape (Set → array). */
function serializeEmpire(e: Empire): SerializedEmpire {
  return {
    id: e.id,
    name: e.name,
    raceId: e.raceId,
    isPlayer: e.isPlayer,
    resources: { ...e.resources },
    planetIds: [...e.planetIds],
    researchedTechs: [...e.researchedTechs],
    activeResearch: e.activeResearch ? { ...e.activeResearch } : null,
    shipIds: [...e.shipIds],
    color: e.color,
    aiDifficulty: e.aiDifficulty,
    aiBehaviour: e.aiBehaviour,
    // v4M3.1 — team membership round-trips verbatim. `null` for the
    // pre-v14 legacy "all solo" default; non-null integer for teamed
    // empires. Save-format version bumped to 14 to mark the schema.
    teamId: e.teamId,
    scoutedSystemIds: [...e.scoutedSystemIds],
    diplomaticFocus: e.diplomaticFocus,
  };
}

/**
 * v3M21.1 — Convert a live Planet (with `influence: Map`) into a
 * JSON-safe SerializedPlanet (with `influence: Array<[id, points]>`).
 * All other fields ride through unchanged via spread.
 */
function serializePlanet(p: Planet): SerializedPlanet {
  return {
    ...p,
    influence: [...p.influence.entries()],
  };
}

function serializeTeamVision(
  teamVision: GameState['teamVision'],
): SerializedTeamVision {
  const out: SerializedTeamVision = [];
  for (const [teamId, inner] of teamVision) {
    const innerArr: Array<[number, VisibilityRecord]> = [];
    for (const [shipId, rec] of inner) innerArr.push([shipId, { ...rec }]);
    out.push([teamId, innerArr]);
  }
  return out;
}

/**
 * Serialise a live GameState into a JSON-safe SaveBlob. The blob is the
 * full input to JSON.stringify — write `JSON.stringify(blob)` to persist.
 *
 * `label` is the player's slot label (named slots) or null (quicksave).
 */
export function serializeGameState(
  state: GameState,
  opts: { label: string | null },
): SaveBlob {
  // Body. Maps walk in insertion order; that's stable in v8 / SpiderMonkey
  // for integer keys, but we don't rely on order semantically — the
  // deserialiser rebuilds Maps from the entry list and order doesn't
  // affect sim behaviour.
  const body: SerializedGameState = {
    version: state.version,
    tick: state.tick,
    rng: { s: state.rng.s },
    nextEntityId: state.nextEntityId,
    empires: [...state.empires.entries()].map(([id, e]) => [id, serializeEmpire(e)]),
    planets: [...state.planets.entries()].map(([id, p]) => [id, serializePlanet(p)]),
    systems: [...state.systems.entries()],
    warpGateLinks: [...state.warpGateLinks.entries()],
    ships: [...state.ships.entries()].map(([id, s]) => [id, serializeShip(s)]),
    missiles: [...state.missiles.entries()],
    structures: [...state.structures.entries()],
    environment: [...state.environment.entries()],
    teamVision: serializeTeamVision(state.teamVision),
    playerEmpireId: state.playerEmpireId,
    gameOutcome: state.gameOutcome,
  };

  // v3M21.1 — exclude the synthetic neutral empire from the AI count.
  // Header surfaces "real AI opponents" only; the neutral row is an
  // implementation detail of the diplomacy system, not a competing AI.
  const aiOpponentCount = [...state.empires.values()].filter(
    (e) => !e.isPlayer && e.id !== EMPIRE_NEUTRAL_ID,
  ).length;

  const header: SaveBlobHeader = {
    formatVersion: SAVE_BLOB_VERSION,
    savedAt: Date.now(),
    tick: state.tick,
    playerEmpireId: state.playerEmpireId,
    aiOpponentCount,
    label: opts.label,
    gameOutcome: state.gameOutcome,
  };

  return { header, body };
}
